import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';

export const DEFAULT_CLIENT_ID = '00000000-0000-0000-0000-000000000000';

export interface TargetingClient {
  id: string;
  name: string;
}

interface Props {
  /** Current value in the backend wire format: [DEFAULT_CLIENT_ID] = global,
   *  any other non-empty array = specific, [] = no selection (invalid). */
  value: string[];
  onChange: (next: string[]) => void;
  clients: TargetingClient[];
  disabled?: boolean;
  /** Text shown inside the trigger when disabled — lets the caller explain
   *  why (e.g. "Pick a device type first"). Falls back to the normal
   *  placeholder if absent. */
  disabledHint?: string;
  /** Renders the control in an error state (red border). Callers typically
   *  pair this with a sibling .field-error message. */
  error?: boolean;
  id?: string;
}

// Show the search box whenever there is at least one client. (Previously
// gated at >6 so small lists hid it; the search is always offered now.)
const SEARCH_THRESHOLD = 0;

/**
 * Single combobox that expresses "template applies to all clients" OR
 * "template applies to a subset of clients". Replaces the earlier
 * radio-plus-checkbox-list layout.
 *
 * Wire model: [DEFAULT_CLIENT_ID] means global, any other array means
 * scoped to those clients. Matches the backend's _select_templates Path 1
 * UNION Path 2 semantics — no translation layer needed.
 */
export const ClientTargetingInput: React.FC<Props> = ({
  value,
  onChange,
  clients,
  disabled,
  disabledHint,
  error,
  id,
}) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const isGlobal = value.includes(DEFAULT_CLIENT_ID);
  const selectedIds = useMemo(
    () => (isGlobal ? [] : value),
    [isGlobal, value],
  );

  const selectedClients = useMemo(
    () => clients.filter((c) => selectedIds.includes(c.id)),
    [clients, selectedIds],
  );

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => c.name.toLowerCase().includes(q));
  }, [clients, search]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Autofocus search when opening (if it's rendered)
  useEffect(() => {
    if (open && clients.length > SEARCH_THRESHOLD) {
      searchRef.current?.focus();
    }
  }, [open, clients.length]);

  const setGlobal = useCallback(() => {
    onChange([DEFAULT_CLIENT_ID]);
  }, [onChange]);

  const toggleClient = useCallback(
    (clientId: string) => {
      if (isGlobal) {
        // Leaving global implicitly — start a specific list with this client.
        onChange([clientId]);
        return;
      }
      if (selectedIds.includes(clientId)) {
        onChange(selectedIds.filter((x) => x !== clientId));
      } else {
        onChange([...selectedIds, clientId]);
      }
    },
    [isGlobal, selectedIds, onChange],
  );

  const removeChip = useCallback(
    (clientId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(selectedIds.filter((x) => x !== clientId));
    },
    [selectedIds, onChange],
  );

  // When disabled the caller can pass a hint ("pick a device type first")
  // that takes precedence over the normal placeholder — makes the blocker
  // obvious without an out-of-band tooltip.
  const placeholderText = disabled && disabledHint
    ? disabledHint
    : t('configTemplates.targetingPlaceholder');
  const summaryText = isGlobal
    ? t('configTemplates.targetAllClients')
    : selectedClients.length === 0
      ? placeholderText
      : null; // chips rendered

  return (
    <div
      ref={rootRef}
      className={`client-targeting${error ? ' error' : ''}${disabled ? ' disabled' : ''}`}
      id={id}
    >
      <button
        type="button"
        className="client-targeting-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="client-targeting-value">
          {isGlobal ? (
            <span className="client-targeting-global">
              <span className="client-targeting-global-icon" aria-hidden="true">
                ●
              </span>
              {summaryText}
            </span>
          ) : selectedClients.length === 0 ? (
            <span className="client-targeting-placeholder">{summaryText}</span>
          ) : (
            <>
              {selectedClients.map((c) => {
                const label = c.name || c.id;
                return (
                  <span
                    className="client-targeting-chip"
                    key={c.id}
                    title={label}
                  >
                    <span className="client-targeting-chip-name">{label}</span>
                    <button
                      type="button"
                      className="client-targeting-chip-remove"
                      onClick={(e) => removeChip(c.id, e)}
                      aria-label={t('configTemplates.removeClient', { name: label })}
                      tabIndex={-1}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </>
          )}
        </div>
        <span className="client-targeting-caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className="client-targeting-popup" role="listbox">
          {clients.length > SEARCH_THRESHOLD && (
            <input
              ref={searchRef}
              type="text"
              className="client-targeting-search"
              placeholder={t('configTemplates.searchClientsPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <button
            type="button"
            className={`client-targeting-option is-global${isGlobal ? ' is-selected' : ''}`}
            onClick={() => {
              setGlobal();
              setOpen(false);
            }}
            role="option"
            aria-selected={isGlobal}
          >
            <span className="client-targeting-option-radio" aria-hidden="true">
              {isGlobal ? '●' : '○'}
            </span>
            <span className="client-targeting-option-label">
              {t('configTemplates.targetAllClients')}
            </span>
          </button>

          <div className="client-targeting-divider" role="separator" />

          {clients.length === 0 ? (
            <div className="client-targeting-empty">
              {t('configTemplates.noClientsAvailable')}
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="client-targeting-empty">
              {t('configTemplates.noMatchingClients')}
            </div>
          ) : (
            filteredClients.map((c) => {
              const checked = selectedIds.includes(c.id);
              const label = c.name || c.id;
              return (
                <label
                  key={c.id}
                  className={`client-targeting-option${checked ? ' is-selected' : ''}`}
                  role="option"
                  aria-selected={checked}
                  title={label}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleClient(c.id)}
                  />
                  <span className="client-targeting-option-label">{label}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ClientTargetingInput;
